// File: src/components/admin/scheduler/SchedulerStatsCards.js
// Dashboard-style stats cards for scheduler overview

import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Skeleton,
    Tooltip,
} from '@mui/material';
import {
    People as UsersIcon,
    PlayCircle as ExecutionsIcon,
    CheckCircle as SuccessIcon,
    Speed as DurationIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';

const StatCard = ({ title, value, subtitle, icon: Icon, color, loading, tooltip, 'data-testid': testId }) => {
    const content = (
        <Card
            sx={{
                flex: 1,
                minWidth: 150,
                borderTop: `3px solid ${color}`,
            }}
            data-testid={testId}
        >
            <CardContent sx={{ pb: '16px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            {title}
                        </Typography>
                        {loading ? (
                            <Skeleton width={60} height={32} />
                        ) : (
                            <Typography variant="h4" fontWeight={600} color={color}>
                                {value}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Icon sx={{ color, fontSize: 32, opacity: 0.7 }} />
                </Box>
            </CardContent>
        </Card>
    );

    if (tooltip) {
        return <Tooltip title={tooltip} arrow>{content}</Tooltip>;
    }

    return content;
};

const SchedulerStatsCards = ({ stats, loading }) => {
    // Calculate values from stats
    const activeUsers = stats?.activeUsersCount || 0;
    const totalExecutions = stats?.period?.totals?.executions || 0;
    const totalFailures = stats?.period?.totals?.failures || 0;
    const successRate = totalExecutions > 0
        ? ((totalExecutions - totalFailures) / totalExecutions * 100).toFixed(1)
        : '100';
    const avgDuration = totalExecutions > 0
        ? ((stats?.period?.totals?.durationMs || 0) / totalExecutions / 1000).toFixed(1)
        : '0';

    // Get today's stats
    const todayStats = stats?.dailyStats?.[0] || { executions: 0, failures: 0 };
    const yesterdayStats = stats?.dailyStats?.[1] || { executions: 0 };
    const executionTrend = todayStats.executions - yesterdayStats.executions;
    const trendText = executionTrend >= 0 ? `+${executionTrend} vs yesterday` : `${executionTrend} vs yesterday`;

    // Count recent errors (last 24h)
    const recentErrors = todayStats.failures || 0;

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }} data-testid="scheduler-stats-cards">
            <StatCard
                title="Active Users"
                value={activeUsers}
                subtitle="with schedules"
                icon={UsersIcon}
                color="#1976d2"
                loading={loading}
                tooltip="Users who have at least one schedule configured"
                data-testid="scheduler-stat-active-users"
            />
            <StatCard
                title="Today's Executions"
                value={todayStats.executions}
                subtitle={trendText}
                icon={ExecutionsIcon}
                color="#2e7d32"
                loading={loading}
                tooltip="Schedule executions run today"
                data-testid="scheduler-stat-executions"
            />
            <StatCard
                title="Success Rate"
                value={`${successRate}%`}
                subtitle={`${stats?.period?.days || 7}d period`}
                icon={SuccessIcon}
                color="#9c27b0"
                loading={loading}
                tooltip={`${totalExecutions} total executions, ${totalFailures} failures`}
                data-testid="scheduler-stat-success-rate"
            />
            <StatCard
                title="Avg Duration"
                value={`${avgDuration}s`}
                subtitle="per execution"
                icon={DurationIcon}
                color="#ed6c02"
                loading={loading}
                tooltip="Average time per schedule execution"
                data-testid="scheduler-stat-avg-duration"
            />
            <StatCard
                title="Errors (24h)"
                value={recentErrors}
                subtitle={recentErrors > 0 ? 'needs attention' : 'all clear'}
                icon={ErrorIcon}
                color={recentErrors > 0 ? '#d32f2f' : '#757575'}
                loading={loading}
                tooltip="Failures in the last 24 hours"
                data-testid="scheduler-stat-errors"
            />
        </Box>
    );
};

export { StatCard };
export default SchedulerStatsCards;
